import { CanvaSX, isTestEnv } from '../canvasx';
import { defaultShapeProps } from '../shapes';
import { drawRect } from '../util';

const imageCache = new Map<string, HTMLImageElement>();

const defaultImageProps = { ...defaultShapeProps };

function drawImageWithObjectFitCover(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  containerWidth: number,
  containerHeight: number
) {
  const imageWidth = image.naturalWidth || image.width;
  const imageHeight = image.naturalHeight || image.height;

  const containerAspectRatio = containerWidth / containerHeight;
  const imageAspectRatio = imageWidth / imageHeight;

  if (imageAspectRatio > containerAspectRatio) {
    // Image is wider than container, crop horizontally
    const scaledImageWidth = imageHeight * containerAspectRatio;
    const cropX = (imageWidth - scaledImageWidth) / 2;
    ctx.drawImage(
      image,
      cropX,
      0,
      scaledImageWidth,
      imageHeight,
      0,
      0,
      containerWidth,
      containerHeight
    );
  } else {
    // Image is taller than container, crop vertically
    const scaledImageHeight = imageWidth / containerAspectRatio;
    const cropY = (imageHeight - scaledImageHeight) / 2;
    ctx.drawImage(
      image,
      0,
      cropY,
      imageWidth,
      scaledImageHeight,
      0,
      0,
      containerWidth,
      containerHeight
    );
  }
}

export type ImageProps = Partial<Omit<ShapeStruct, 'borderRadius'>> &
  DimensionStruct & {
    // For now, images require fixed dimensions, as it would complicate the layout/dimension calculation
    width: number;
    height: number;

    // Other image props
    crossOrigin?: boolean;
    objectFitCover?: boolean;
    rounded?: boolean;
    src: string;
  };

export const Image: CanvaSXComponent<
  ImageProps,
  Omit<typeof defaultImageProps, 'borderRadius'>
> = function (props) {
  const {
    crossOrigin = false,
    objectFitCover = false,
    src,
    width,
    height,
    rounded = false,
  } = props;
  const { canvas, ctx, object } = CanvaSX.context;

  let image = imageCache.get(src);
  if (!image) {
    image = new window.Image();

    image.addEventListener('error', () => {
      image?.setAttribute('hasError', '1');
    });

    // TODO: default dev env loads avatars through S3, and does not have CORS headers currently
    if (window.railsEnv !== 'development' && crossOrigin) {
      image.crossOrigin = 'anonymous';
    }

    image.addEventListener('load', async () => {
      await image?.decode();
      object.controller?.renderManager?.refreshObject(
        object as unknown as fabric.Object
      );
      canvas.requestRenderAll();
    });

    image.src = src;
    imageCache.set(src, image);
  }

  drawRect(ctx, { ...props, width, height }, () => {
    const renderImage =
      isTestEnv || (image?.complete && !image.getAttribute('hasError'));

    if (renderImage) {
      ctx.save();

      if (rounded) {
        ctx.beginPath();
        ctx.arc(width / 2, height / 2, width / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
      }

      // Catch and suppress errors
      try {
        // If the image is not cross-origin, and it's from a different origin, we can't render it while exporting
        const skipRender =
          !crossOrigin &&
          canvas.exporting &&
          !src.startsWith(location.origin) &&
          !src.startsWith('data:');

        if (!skipRender) {
          if (isTestEnv) {
            ctx.save();
            ctx.fillStyle = 'red';
            ctx.fillRect(0, 0, width, height);
            ctx.restore();
          } else if (image) {
            if (objectFitCover) {
              drawImageWithObjectFitCover(ctx, image, width, height);
            } else {
              ctx.drawImage(image, 0, 0, width, height);
            }
          } else {
            console.error('Not rendering image.');
          }
        }
      } catch (ex) {
        console.error('Error rendering image to canvas:', ex);
      }

      ctx.restore();
    }
  });

  return null;
};

Image.defaultProps = defaultImageProps;
